import { css } from '@emotion/react';
import { BREAKPOINTS, Divider, IconButton, Select, semanticTokens, SvgIcon, tokens } from '@flysas-tech/commercial-ui';
import { ProgressStep, ResponsiveContainer, useMediaQuery } from 'common';
import { t } from 'i18n';
import React, { Fragment, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

const styles = {
  desktop: css`
    all: unset;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    gap: ${tokens.dimension['space-4']};
  `,
  stepInfo: css`
    all: unset;
    display: flex;
    align-items: center;
    gap: ${tokens.dimension['space-2']};
  `,
  link: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.primary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  `,
  label: (isCurrentStep: boolean) => css`
    font-weight: ${isCurrentStep ? 700 : 400};
    white-space: nowrap;
  `,
  comingStep: css`
    all: unset;
    pointer-events: none;
    color: ${semanticTokens.color.text.primary};
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
  `,
  icon: (isCompleted: boolean) => css`
    color: ${isCompleted ? semanticTokens.color.icon.primary : semanticTokens.color.border.neutral};
  `,
  divider: css`
    width: 100%;
    flex-shrink: 2;

    &:last-of-type {
      display: none;
    }
  `,
  mobileContent: css`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 ${tokens.dimension['space-4']};
  `,
  mobileSelectContainer: css`
    margin-left: auto;
    margin-right: auto;
    select {
      padding: ${tokens.dimension['space-4']};
    }
    div {
      background-color: transparent;
    }
  `,
  mobileIcon: css`
    color: ${semanticTokens.color.icon.primary};
  `,
};

type ProgressBarProps = {
  steps: ProgressStep[];
  currentStepIndex: number;
  navigate: (path: string) => () => void;
};

export function ProgressBar({ steps, currentStepIndex, navigate }: ProgressBarProps) {
  const isMobile = useMediaQuery({ max: BREAKPOINTS.layout.md });

  // hide progress bar if step is not defined (i.e meals)
  if (currentStepIndex === -1) return null;

  // hide progress bar if on upsell
  if (currentStepIndex === 0) return null;

  return isMobile ? (
    <div>
      <MobileProgressBar navigate={navigate} steps={steps} currentStepIndex={currentStepIndex} />
    </div>
  ) : (
    <ResponsiveContainer fullWidth>
      <nav>
        <ol css={styles.desktop}>
          <DesktopProgressBar navigate={navigate} steps={steps} currentStepIndex={currentStepIndex} />
        </ol>
      </nav>
    </ResponsiveContainer>
  );
}

const getIcon = (isCompleted: boolean, isCurrentStep: boolean) => {
  if (isCompleted) {
    return 'Check';
  } else if (isCurrentStep) {
    return 'RadioFilled';
  }
  return 'RadioBlank';
};

function DesktopProgressBar({ steps, currentStepIndex, navigate }: ProgressBarProps) {
  return steps.map((step, index) => {
    const isCurrentStep = index === currentStepIndex;
    const isCompleted = index < currentStepIndex;

    return (
      <Fragment key={`${step.name}-${index}`}>
        <li css={styles.stepInfo}>
          <SvgIcon
            icon={getIcon(isCompleted, isCurrentStep)}
            size="xs"
            ariaHidden
            css={styles.icon(isCompleted || isCurrentStep)}
          />

          {isCompleted ? (
            <NavLink
              to={step.path}
              onClick={e => {
                e.preventDefault();
                navigate(step.path);
              }}
              css={[styles.link, styles.label(isCurrentStep)]}
              data-testid={step.testid}
            >
              {step.label}
            </NavLink>
          ) : (
            <a
              css={[styles.comingStep, styles.label(isCurrentStep)]}
              aria-current={isCurrentStep ? 'step' : undefined}
              data-testid={step.testid}
            >
              {step.label}
            </a>
          )}
        </li>

        <div css={styles.divider}>
          <Divider direction="horizontal" variant="subtle" />
        </div>
      </Fragment>
    );
  });
}

function MobileProgressBar({ steps, currentStepIndex, navigate }: ProgressBarProps) {
  const [selectedValue, setSelectedValue] = useState(steps[currentStepIndex].path);
  const showBackArrow = currentStepIndex > 0;
  const totalSteps = steps.length;
  const options = steps.map(({ label, path }, index) => ({
    label: `Steg ${index + 1} as ${totalSteps} - ${label}`,
    value: path,
  }));

  useEffect(() => {
    // Sync selected value with current step
    setSelectedValue(steps[currentStepIndex].path);
  }, [currentStepIndex]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const path = e.target.value;
    const stepIndex = steps.findIndex(step => step.path === path);

    if (stepIndex < currentStepIndex) {
      setSelectedValue(path);
      navigate(path);
    } else {
      setSelectedValue(steps[currentStepIndex].path);
    }
  };

  return (
    <div css={styles.mobileContent}>
      {showBackArrow && (
        <div>
          <IconButton
            data-testid="progress-bar-back-button"
            ariaLabel={t('common:back')}
            variant="link"
            size="medium"
            icon={'Arrow'}
            rotation={270}
            onClick={() => navigate(steps[currentStepIndex - 1].path)}
            css={styles.mobileIcon}
          />
        </div>
      )}

      <div css={styles.mobileSelectContainer}>
        <Select.Root
          data-testid="progress-bar-select"
          variant="secondary"
          value={selectedValue}
          onChange={handleChange}
        >
          {options.map((option, index) => (
            <Select.Option key={index} label={option.label} value={option.value}></Select.Option>
          ))}
        </Select.Root>
      </div>
    </div>
  );
}
